import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth-compat";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useRole } from "@/contexts/RoleContext";
import { useStudentProfile } from "@/contexts/StudentProfileContext";
import {
  switchDefaultRole,
  setDefaultRole,
  type Role,
} from "@/services/UserManagmentServices";
import { ROLE_DISPLAY, ROLE_ONBOARDING_ROUTE } from "@/lib/roleDisplay";

/**
 * Centralises the "switch active role" behaviour that previously lived inline in
 * RoleSwitchPage, so the sidebar account switcher (and the fallback page) share it.
 *
 * - `switchRole` — instant switch to a role the user already has: persist the new
 *   default, refresh the cached profile, mirror it into RoleContext, then land on
 *   that role's dashboard.
 * - `acquireRole` — for a role the user does NOT have yet ("Set up … mode"): route
 *   the user straight into that role's onboarding WITHOUT creating the role yet.
 *   The role is only created once the user submits the first required piece of
 *   onboarding info (see `ensureRole`), so abandoning the flow midway leaves no
 *   half-created secondary account behind.
 * - `ensureRole` — called by the onboarding surfaces at the moment the user
 *   submits their first required info: creates `target` for the current user
 *   if they don't already have it. Calls `POST /me/role` first (required for
 *   STUDENT — it's the only endpoint that resolves registration geo into
 *   country/currency on the new profile), then `PUT /me/default-role` to force
 *   it active (POST /me/role only defaults a role when it's the user's first
 *   one; the PUT is a no-op when it was already default). Refreshes the cached
 *   profile and mirrors the active role. Idempotent.
 *
 * `pending` holds the role currently being switched to (for spinners); `null` when idle.
 */
export function useSwitchRole() {
  const navigate = useNavigate();
  const auth = useAuth();
  const { user, refetch } = useCurrentUser();
  const { setRole } = useRole();
  const { refreshProfile } = useStudentProfile();
  const [pending, setPending] = useState<Role | null>(null);

  const switchRole = useCallback(
    async (target: Role) => {
      if (pending) return;
      setPending(target);
      try {
        const token = await auth.getToken();
        if (!token) {
          navigate("/login");
          return;
        }
        await switchDefaultRole(token, target);
        await refetch();
        setRole(target.toLowerCase());
        toast.success(`Switched to ${ROLE_DISPLAY[target].label}`);
        navigate(ROLE_DISPLAY[target].dashboard);
      } catch {
        toast.error("Couldn't switch mode. Please try again.");
      } finally {
        setPending(null);
      }
    },
    [pending, auth, refetch, setRole, navigate],
  );

  /**
   * Create `target` for the current user if they don't already have it, then
   * mirror it into RoleContext. Returns true once the role is present and
   * active (caller should abort on false). Idempotent.
   */
  const ensureRole = useCallback(
    async (target: Role): Promise<boolean> => {
      const alreadyHas = user?.roles?.some((r) => r.role === target) ?? false;
      if (alreadyHas) {
        setRole(target.toLowerCase());
        return true;
      }
      const token = await auth.getToken();
      if (!token) {
        navigate("/login");
        return false;
      }
      // POST /me/role creates the role and — for STUDENT — resolves
      // registration geo into country/currency on the new profile.
      await setDefaultRole(token, target);
      // It only marks the new role default when it's the user's first role,
      // so force it active; a no-op if it was already made default above.
      await switchDefaultRole(token, target);
      await refetch();
      // refetch() above only refreshes the ['currentUser'] query cache — it
      // does not touch StudentProfileContext, which ran its one-shot fetch at
      // app mount (before this role existed) and never re-runs on its own.
      // Force it to pull the freshly created STUDENT profile (with geo-resolved
      // country/currency) now, so surfaces like MyProfilePage aren't stale
      // until a hard refresh. Only STUDENT has a profile behind this endpoint.
      if (target === "STUDENT") {
        await refreshProfile(true);
      }
      setRole(target.toLowerCase());
      return true;
    },
    [user, auth, refetch, refreshProfile, setRole, navigate],
  );

  const acquireRole = useCallback(
    async (target: Role) => {
      if (pending) return;
      // Do NOT create the role here. Route the user into the target role's
      // onboarding; the role is created only when they submit their first
      // required info (see `ensureRole`). Abandoning the flow before that
      // leaves no half-created secondary account.
      navigate(ROLE_ONBOARDING_ROUTE[target], { replace: true });
    },
    [pending, navigate],
  );

  return { switchRole, acquireRole, ensureRole, pending };
}
